/* AI Quality Program — continuous-review animation.
   Ported from the design bundle's firewall.jsx.
   Technicians → AI Quality Team → The Platform. Most inspections pass through
   (verified); some are flagged and bounce back to the field for rework.
   Self-contained: injects structural styles + motion keyframes, reads Orbix tokens via var(). */

import React, { useMemo } from 'react';

const FW_SEVERITY = {
  ok:   { fwd: '#A78BFA', ret: null,      final: '#19D6A1' },
  warn: { fwd: '#A78BFA', ret: '#FFC27D', final: '#19D6A1' },
  crit: { fwd: '#A78BFA', ret: '#EF4444', final: '#19D6A1' },
};

function FwLegendDot({ color, label }) {
  return (
    <div className="fw-legend-item">
      <span className="fw-legend-dot" style={{ background: color, boxShadow: `0 0 12px ${color}` }} />
      <span>{label}</span>
    </div>
  );
}

function FwLaneGuide({ x1, x2, y, variant }) {
  return (
    <div
      style={{
        position: 'absolute',
        left: Math.min(x1, x2),
        top: y - 1,
        width: Math.abs(x2 - x1),
        height: 2,
        borderTop: variant === 'return'
          ? '1px dashed rgba(255,255,255,0.08)'
          : '1px dashed rgba(198,172,255,0.22)',
      }}
    />
  );
}

function FwLaneLabel({ x, y, text, color }) {
  return (
    <div className="fw-lane-label" style={{ left: x, top: y, color: color || 'rgba(255,255,255,0.55)' }}>
      {text}
    </div>
  );
}

function FwNodeBox({ cx, cy, w, h, className, children }) {
  return (
    <div className={`fw-node ${className || ''}`} style={{ left: cx - w / 2, top: cy - h / 2, width: w, height: h }}>
      {children}
    </div>
  );
}

function FwNodeTech({ cx, cy }) {
  return (
    <FwNodeBox cx={cx} cy={cy} w={180} h={220} className="fw-node-tech">
      <div className="fw-node-inner">
        <svg width="52" height="52" viewBox="0 0 72 72" fill="none"
          stroke="#C6ACFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="36" cy="24" r="10" />
          <path d="M14 60c2-10 11-16 22-16s20 6 22 16" />
          <path d="M22 52l-3 6M50 52l3 6" />
        </svg>
        <div className="fw-node-title">Technicians<br />&amp; Crews</div>
        <div className="fw-node-sub">In the field</div>
      </div>
    </FwNodeBox>
  );
}

function FwNodeAI({ cx, cy }) {
  return (
    <FwNodeBox cx={cx} cy={cy} w={240} h={240} className="fw-node-ai">
      <div className="fw-pulse fw-pulse-1" />
      <div className="fw-pulse fw-pulse-2" />
      <div className="fw-ai-orb">
        <div className="fw-ai-highlight" />
        <div className="fw-ai-label">AI Quality<br />Team</div>
      </div>
    </FwNodeBox>
  );
}

function FwNodePlatform({ cx, cy }) {
  return (
    <FwNodeBox cx={cx} cy={cy} w={180} h={220} className="fw-node-platform">
      <div className="fw-node-inner">
        <svg width="52" height="52" viewBox="0 0 72 72" fill="none"
          stroke="#6EE7B7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <ellipse cx="36" cy="18" rx="22" ry="7" />
          <path d="M14 18v14c0 4 10 7 22 7s22-3 22-7V18" />
          <path d="M14 32v14c0 4 10 7 22 7s22-3 22-7V32" />
          <path d="M14 46v6c0 4 10 7 22 7s22-3 22-7v-6" />
        </svg>
        <div className="fw-node-title">The<br />Platform</div>
        <div className="fw-node-sub" style={{ color: '#6EE7B7' }}>System of record</div>
      </div>
    </FwNodeBox>
  );
}

function FwPacketGlyph({ kind }) {
  const s = {
    width: 18, height: 18, viewBox: '0 0 24 24',
    fill: 'none', stroke: '#160A2E', strokeWidth: 2,
    strokeLinecap: 'round', strokeLinejoin: 'round',
  };
  if (kind === 'doc') return (
    <svg {...s}><path d="M7 4h8l4 4v12H7z" /><path d="M15 4v4h4" /><path d="M10 13h6M10 16h4" /></svg>
  );
  if (kind === 'scan') return (
    <svg {...s}>
      <path d="M4 8V5a1 1 0 011-1h3M16 4h3a1 1 0 011 1v3M20 16v3a1 1 0 01-1 1h-3M8 20H5a1 1 0 01-1-1v-3" />
      <path d="M4 12h16" />
    </svg>
  );
  return (
    <svg {...s}><rect x="4" y="5" width="16" height="14" rx="1.5" />
      <circle cx="9" cy="10" r="1.8" /><path d="M4 17l5-4 4 3 3-3 4 4" /></svg>
  );
}

function FwPacket({ p, cycle }) {
  const sev = FW_SEVERITY[p.severity];
  const isFlagged = p.severity !== 'ok';
  const animName = p.severity === 'ok'
    ? (p.track === 'data' ? 'fw-data-ok' : 'fw-docs-ok')
    : (p.track === 'data' ? 'fw-data-flag' : 'fw-docs-flag');

  return (
    <div
      className="fw-packet"
      style={{
        background: sev.fwd,
        boxShadow: `0 0 18px ${sev.fwd}, 0 4px 8px rgba(0,0,0,0.4)`,
        animation: `${animName} ${cycle}s linear infinite`,
        animationDelay: `${-p.delay}s`,
      }}
    >
      <FwPacketGlyph kind={p.kind} />
      {isFlagged && (
        <div
          className="fw-flag"
          style={{
            background: sev.ret,
            boxShadow: `0 0 14px ${sev.ret}`,
            animation: `fw-flag-show-${p.track} ${cycle}s linear infinite`,
            animationDelay: `${-p.delay}s`,
          }}
        >
          !
        </div>
      )}
    </div>
  );
}

function firewallCss(P) {
  const {
    TECH_X, AI_X, PLAT_X,
    Y_DATA_FWD, Y_DOCS_FWD, Y_DATA_RET, Y_DOCS_RET,
    Y_PLAT_TOP, Y_PLAT_BOT,
  } = P;
  const at = (x, y) => `translate(${x - 18}px, ${y - 18}px)`;

  return `
    .fw-stage-wrap { display: flex; flex-direction: column; align-items: center; gap: 22px; }
    .fw-stage { position: relative; margin: 0 auto; }

    .fw-lane-label {
      position: absolute; transform: translateX(-50%);
      font-family: var(--orbix-font-ui); font-size: 11px; letter-spacing: 0.05em;
      white-space: nowrap;
    }

    .fw-node {
      position: absolute; box-sizing: border-box; border-radius: var(--orbix-r-lg);
      display: flex; align-items: center; justify-content: center;
    }
    .fw-node-tech, .fw-node-platform {
      background: rgba(255,255,255,0.045);
      border: 1px solid rgba(198,172,255,0.26);
    }
    .fw-node-platform { border-color: rgba(110,231,183,0.30); background: rgba(110,231,183,0.05); }
    .fw-node-inner {
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      gap: 12px; text-align: center; padding: 0 14px;
    }
    .fw-node-title { font-family: var(--orbix-font-display); font-weight: 700; font-size: 18px; color: #fff; line-height: 1.15; }
    .fw-node-sub {
      font-family: var(--orbix-font-ui); font-size: 10.5px; letter-spacing: 0.14em;
      text-transform: uppercase; color: rgba(255,255,255,0.5); margin-top: -2px;
    }

    .fw-node-ai { overflow: visible; }
    .fw-pulse {
      position: absolute; top: 50%; left: 50%; width: 158px; height: 158px;
      margin: -79px 0 0 -79px; border-radius: 50%;
      border: 1px solid rgba(129,107,255,0.5);
    }
    .fw-pulse-1 { animation: fw-pulse 3s ease-out infinite; }
    .fw-pulse-2 { animation: fw-pulse 3s ease-out infinite 1.5s; }
    .fw-ai-orb {
      position: absolute; top: 50%; left: 50%; width: 158px; height: 158px;
      margin: -79px 0 0 -79px; border-radius: 50%;
      background: radial-gradient(circle at 34% 30%, #C4B5FD 0%, #6C5CE0 44%, #2E2A63 100%);
      box-shadow: 0 0 54px rgba(108,92,224,0.6), inset -14px -14px 30px rgba(30,22,70,0.8);
      display: flex; align-items: center; justify-content: center; overflow: hidden;
    }
    .fw-ai-highlight {
      position: absolute; top: 16%; left: 20%; width: 40%; height: 32%; border-radius: 50%;
      background: radial-gradient(circle, rgba(255,255,255,0.55), transparent 70%); filter: blur(3px);
    }
    .fw-ai-label {
      position: relative; font-family: var(--orbix-font-display); font-weight: 700;
      font-size: 19px; line-height: 1.14; text-align: center; color: #fff;
      text-shadow: 0 2px 8px rgba(0,0,0,0.5);
    }

    .fw-packet {
      position: absolute; left: 0; top: 0; width: 36px; height: 36px; border-radius: 9px;
      display: grid; place-items: center; z-index: 5; will-change: transform;
    }
    .fw-flag {
      position: absolute; top: -5px; right: -5px; width: 17px; height: 17px; border-radius: 50%;
      display: grid; place-items: center;
      font-family: var(--orbix-font-ui); font-weight: 800; font-size: 11px; color: #160A2E;
    }

    .fw-legend { display: flex; flex-wrap: wrap; gap: 22px; justify-content: center; }
    .fw-legend-item {
      display: inline-flex; align-items: center; gap: 9px;
      font-family: var(--orbix-font-ui); font-size: 13px; color: var(--orbix-fg-on-dark-dim);
    }
    .fw-legend-dot { width: 11px; height: 11px; border-radius: 50%; display: inline-block; }

    @keyframes fw-pulse {
      0%   { transform: scale(0.95); opacity: 0.7; }
      100% { transform: scale(1.28); opacity: 0; }
    }

    @keyframes fw-data-ok {
      0%   { transform: ${at(TECH_X + 80, Y_DATA_FWD)}; opacity: 0; background: #A78BFA; box-shadow: 0 0 18px #A78BFA, 0 4px 8px rgba(0,0,0,0.4); }
      3%   { opacity: 1; }
      15%  { transform: ${at(AI_X - 100, Y_DATA_FWD)}; opacity: 1; background: #A78BFA; }
      20%  { transform: ${at(AI_X, Y_DATA_FWD)}; opacity: 0.7; background: #A78BFA; }
      22%  { transform: ${at(AI_X, Y_DATA_FWD)}; opacity: 1; background: #19D6A1; box-shadow: 0 0 22px #19D6A1, 0 4px 8px rgba(0,0,0,0.4); }
      35%  { transform: ${at(PLAT_X - 100, Y_PLAT_TOP)}; opacity: 1; background: #19D6A1; box-shadow: 0 0 22px #19D6A1, 0 4px 8px rgba(0,0,0,0.4); }
      40%  { transform: ${at(PLAT_X - 100, Y_PLAT_TOP)}; opacity: 0; background: #19D6A1; }
      100% { transform: ${at(PLAT_X - 100, Y_PLAT_TOP)}; opacity: 0; background: #19D6A1; }
    }

    @keyframes fw-docs-ok {
      0%   { transform: ${at(TECH_X + 80, Y_DOCS_FWD)}; opacity: 0; background: #A78BFA; box-shadow: 0 0 18px #A78BFA, 0 4px 8px rgba(0,0,0,0.4); }
      3%   { opacity: 1; }
      15%  { transform: ${at(AI_X - 100, Y_DOCS_FWD)}; opacity: 1; background: #A78BFA; }
      20%  { transform: ${at(AI_X, Y_DOCS_FWD)}; opacity: 0.7; background: #A78BFA; }
      22%  { transform: ${at(AI_X, Y_DOCS_FWD)}; opacity: 1; background: #19D6A1; box-shadow: 0 0 22px #19D6A1, 0 4px 8px rgba(0,0,0,0.4); }
      35%  { transform: ${at(PLAT_X - 100, Y_PLAT_BOT)}; opacity: 1; background: #19D6A1; box-shadow: 0 0 22px #19D6A1, 0 4px 8px rgba(0,0,0,0.4); }
      40%  { transform: ${at(PLAT_X - 100, Y_PLAT_BOT)}; opacity: 0; background: #19D6A1; }
      100% { transform: ${at(PLAT_X - 100, Y_PLAT_BOT)}; opacity: 0; background: #19D6A1; }
    }

    @keyframes fw-data-flag {
      0%   { transform: ${at(TECH_X + 80, Y_DATA_FWD)}; opacity: 0; background: #A78BFA; box-shadow: 0 0 18px #A78BFA, 0 4px 8px rgba(0,0,0,0.4); }
      3%   { opacity: 1; }
      15%  { transform: ${at(AI_X - 100, Y_DATA_FWD)}; opacity: 1; background: #A78BFA; }
      17%  { transform: ${at(AI_X, Y_DATA_FWD)}; opacity: 1; background: #EF4444; box-shadow: 0 0 22px #EF4444, 0 4px 8px rgba(0,0,0,0.4); }
      22%  { transform: ${at(AI_X - 80, Y_DATA_RET)}; opacity: 1; background: #EF4444; }
      30%  { transform: ${at(TECH_X + 80, Y_DATA_RET)}; opacity: 1; background: #EF4444; }
      33%  { transform: ${at(TECH_X + 80, Y_DATA_RET)}; opacity: 1; background: #EF4444; }
      35%  { transform: ${at(TECH_X + 80, Y_DATA_FWD)}; opacity: 1; background: #A78BFA; box-shadow: 0 0 18px #A78BFA, 0 4px 8px rgba(0,0,0,0.4); }
      46%  { transform: ${at(AI_X - 100, Y_DATA_FWD)}; opacity: 1; background: #A78BFA; }
      50%  { transform: ${at(AI_X, Y_DATA_FWD)}; opacity: 1; background: #19D6A1; box-shadow: 0 0 22px #19D6A1, 0 4px 8px rgba(0,0,0,0.4); }
      62%  { transform: ${at(PLAT_X - 100, Y_PLAT_TOP)}; opacity: 1; background: #19D6A1; box-shadow: 0 0 22px #19D6A1, 0 4px 8px rgba(0,0,0,0.4); }
      67%  { transform: ${at(PLAT_X - 100, Y_PLAT_TOP)}; opacity: 0; background: #19D6A1; }
      100% { transform: ${at(PLAT_X - 100, Y_PLAT_TOP)}; opacity: 0; background: #19D6A1; }
    }

    @keyframes fw-docs-flag {
      0%   { transform: ${at(TECH_X + 80, Y_DOCS_FWD)}; opacity: 0; background: #A78BFA; box-shadow: 0 0 18px #A78BFA, 0 4px 8px rgba(0,0,0,0.4); }
      3%   { opacity: 1; }
      15%  { transform: ${at(AI_X - 100, Y_DOCS_FWD)}; opacity: 1; background: #A78BFA; }
      17%  { transform: ${at(AI_X, Y_DOCS_FWD)}; opacity: 1; background: #FFC27D; box-shadow: 0 0 22px #FFC27D, 0 4px 8px rgba(0,0,0,0.4); }
      22%  { transform: ${at(AI_X - 80, Y_DOCS_RET)}; opacity: 1; background: #FFC27D; }
      30%  { transform: ${at(TECH_X + 80, Y_DOCS_RET)}; opacity: 1; background: #FFC27D; }
      33%  { transform: ${at(TECH_X + 80, Y_DOCS_RET)}; opacity: 1; background: #FFC27D; }
      35%  { transform: ${at(TECH_X + 80, Y_DOCS_FWD)}; opacity: 1; background: #A78BFA; box-shadow: 0 0 18px #A78BFA, 0 4px 8px rgba(0,0,0,0.4); }
      46%  { transform: ${at(AI_X - 100, Y_DOCS_FWD)}; opacity: 1; background: #A78BFA; }
      50%  { transform: ${at(AI_X, Y_DOCS_FWD)}; opacity: 1; background: #19D6A1; box-shadow: 0 0 22px #19D6A1, 0 4px 8px rgba(0,0,0,0.4); }
      62%  { transform: ${at(PLAT_X - 100, Y_PLAT_BOT)}; opacity: 1; background: #19D6A1; box-shadow: 0 0 22px #19D6A1, 0 4px 8px rgba(0,0,0,0.4); }
      67%  { transform: ${at(PLAT_X - 100, Y_PLAT_BOT)}; opacity: 0; background: #19D6A1; }
      100% { transform: ${at(PLAT_X - 100, Y_PLAT_BOT)}; opacity: 0; background: #19D6A1; }
    }

    @keyframes fw-flag-show-data {
      0%, 16% { opacity: 0; transform: scale(0.3); }
      18%     { opacity: 1; transform: scale(1.15); }
      34%     { opacity: 1; transform: scale(1); }
      36%     { opacity: 0; transform: scale(0.3); }
      100%    { opacity: 0; transform: scale(0.3); }
    }
    @keyframes fw-flag-show-docs {
      0%, 16% { opacity: 0; transform: scale(0.3); }
      18%     { opacity: 1; transform: scale(1.15); }
      34%     { opacity: 1; transform: scale(1); }
      36%     { opacity: 0; transform: scale(0.3); }
      100%    { opacity: 0; transform: scale(0.3); }
    }
  `;
}

function FirewallStage() {
  const STAGE_W = 1200;
  const STAGE_H = 480;
  const TECH_X = 140;
  const AI_X = 600;
  const PLAT_X = 1060;
  const Y_DATA_FWD = 150;
  const Y_DOCS_FWD = 330;
  const Y_DATA_RET = 80;
  const Y_DOCS_RET = 400;
  const Y_PLAT_TOP = 150;
  const Y_PLAT_BOT = 330;
  const positions = { TECH_X, AI_X, PLAT_X, Y_DATA_FWD, Y_DOCS_FWD, Y_DATA_RET, Y_DOCS_RET, Y_PLAT_TOP, Y_PLAT_BOT };

  const packets = useMemo(() => [
    { id: 1,  track: 'data', severity: 'ok',   delay:  0.0, kind: 'img'  },
    { id: 2,  track: 'docs', severity: 'ok',   delay:  1.2, kind: 'doc'  },
    { id: 3,  track: 'data', severity: 'warn', delay:  2.4, kind: 'img'  },
    { id: 4,  track: 'docs', severity: 'ok',   delay:  3.6, kind: 'doc'  },
    { id: 5,  track: 'data', severity: 'ok',   delay:  4.8, kind: 'scan' },
    { id: 6,  track: 'docs', severity: 'crit', delay:  6.0, kind: 'doc'  },
    { id: 7,  track: 'data', severity: 'ok',   delay:  7.2, kind: 'img'  },
    { id: 8,  track: 'docs', severity: 'ok',   delay:  8.4, kind: 'doc'  },
    { id: 9,  track: 'data', severity: 'warn', delay:  9.6, kind: 'scan' },
    { id: 10, track: 'docs', severity: 'ok',   delay: 10.8, kind: 'doc'  },
  ], []);
  const CYCLE = 13;

  return (
    <div className="fw-stage-wrap">
      <div className="fw-stage" style={{ width: STAGE_W, height: STAGE_H }}>
        <FwLaneGuide x1={TECH_X + 80} x2={AI_X - 90} y={Y_DATA_FWD} />
        <FwLaneGuide x1={TECH_X + 80} x2={AI_X - 90} y={Y_DOCS_FWD} />
        <FwLaneGuide x1={AI_X + 90}   x2={PLAT_X - 90} y={Y_PLAT_TOP} />
        <FwLaneGuide x1={AI_X + 90}   x2={PLAT_X - 90} y={Y_PLAT_BOT} />
        <FwLaneGuide x1={TECH_X + 80} x2={AI_X - 90} y={Y_DATA_RET} variant="return" />
        <FwLaneGuide x1={TECH_X + 80} x2={AI_X - 90} y={Y_DOCS_RET} variant="return" />

        <FwLaneLabel x={(TECH_X + AI_X) / 2} y={Y_DATA_FWD - 26} text="Inspection data · images · scans" />
        <FwLaneLabel x={(TECH_X + AI_X) / 2} y={Y_DOCS_FWD + 16} text="Records · reports · documents" />
        <FwLaneLabel x={(AI_X + PLAT_X) / 2} y={Y_PLAT_TOP - 26} text="Verified → saved" color="#19D6A1" />
        <FwLaneLabel x={(AI_X + PLAT_X) / 2} y={Y_PLAT_BOT + 16} text="Verified → saved" color="#19D6A1" />

        <FwNodeTech     cx={TECH_X} cy={STAGE_H / 2} />
        <FwNodeAI       cx={AI_X}   cy={STAGE_H / 2} />
        <FwNodePlatform cx={PLAT_X} cy={STAGE_H / 2} />

        {packets.map((p) => (
          <FwPacket key={p.id} p={p} cycle={CYCLE} />
        ))}

        <style dangerouslySetInnerHTML={{ __html: firewallCss(positions) }} />
      </div>

      <div className="fw-legend">
        <FwLegendDot color="#A78BFA" label="Incoming" />
        <FwLegendDot color="#FFC27D" label="Issue flagged" />
        <FwLegendDot color="#EF4444" label="Critical" />
        <FwLegendDot color="#19D6A1" label="Verified · saved" />
      </div>
    </div>
  );
}

export default FirewallStage;
